// State rules packs. Every store belongs to one US state, and the state decides the alcohol rules the
// POS enforces: sales tax default, the below-cost rule, wholesaler credit terms, price-list and rebate
// (RIP) rules, cash rounding and what the licence and brand registration are called.
//
// A state is "supported" only once its pack has been written and checked by someone who knows that
// state's law. Stores can only be created in supported states; the rest show as coming soon.

import { RIP_LIMITS, type RipLimits } from './cpl.js';
import { CREDIT_DAYS } from './credit.js';
import type { IsoDate } from './dates.js';

/**
 * License states: privately owned stores buy from licensed wholesalers.
 * Control states: the state itself sells at retail and/or wholesale (spirits, sometimes wine).
 */
export type AlcoholSystem = 'license' | 'control';

export interface StateRulesPack {
  code: string;
  name: string;
  /** True until someone who knows this state's alcohol law has checked the pack. Shown on store screens. */
  needsLegalCheck: boolean;
  /** Default sales tax rate for new stores, in millionths (6.625% = 66250). Owners can change it. */
  defaultTaxRatePpm: number;
  /** Shown next to the tax rate when the rate depends on where the store is (county or city). */
  localTaxNote?: string;
  /** What the store's alcohol licence is called on screens and purchase orders ("NJ licence"). */
  licenseLabel: string;
  /**
   * Retail sales below legal cost are blocked unless a close-out is approved. null = no such rule.
   * `bottleAddOnCents` is added to the invoice cost per bottle where the state's minimum bottle price
   * is the case price divided by the pack plus a fixed amount by size (Connecticut).
   */
  belowCost: { citation: string; bottleAddOnCents?: (sizeMl: number | null) => number } | null;
  /** Longest credit a wholesaler may give before C.O.D. null = no state limit. */
  wholesalerCredit: { days: number; citation: string } | null;
  /** Monthly wholesaler price list (NJ: Current Price List). null = the state has no posting. */
  priceList: { name: string; shortName: string } | null;
  /** Retail incentive (rebate) programs and their limits. null = not used in this state. */
  rip: { citation: string; limits: Readonly<RipLimits> } | null;
  /** Cash rounding to 5¢ is allowed (from `from`, if set), with the notice the store must post. null = not allowed. */
  cashRounding: { notice: string; from?: IsoDate } | null;
  /** State brand registration number on products and price lists. null = none. */
  brandRegistration: { label: string } | null;
}

export const NJ_RULES: StateRulesPack = {
  code: 'NJ',
  name: 'New Jersey',
  needsLegalCheck: false,
  defaultTaxRatePpm: 66250,
  licenseLabel: 'NJ licence',
  belowCost: { citation: 'N.J.A.C. 13:2-24.8' },
  wholesalerCredit: { days: CREDIT_DAYS, citation: 'N.J.A.C. 13:2-24.4' },
  priceList: { name: 'Current Price List', shortName: 'CPL' },
  rip: { citation: 'N.J.A.C. 13:2-24.1', limits: RIP_LIMITS },
  cashRounding: { notice: 'post a notice at the register, per NJ Consumer Affairs' },
  brandRegistration: { label: 'NJ brand registration' },
};

// New York. Liquor stores sell liquor and wine only; wholesalers file monthly price schedules with the
// SLA. Rebates and allowances to retailers are banned except quantity discounts (ABC Law §101-b(2)),
// so there are no RIPs. A 5¢ cash rounding bill (A9274A / S8580A) is not law yet.
export const NY_RULES: StateRulesPack = {
  code: 'NY',
  name: 'New York',
  needsLegalCheck: true,
  defaultTaxRatePpm: 80000,
  localTaxNote: 'New York sales tax is 4% state plus the county or city rate (8.875% in New York City). Set this store’s combined rate.',
  licenseLabel: 'NY licence',
  belowCost: { citation: 'N.Y. ABC Law §101-bb' },
  wholesalerCredit: { days: 30, citation: 'N.Y. ABC Law §101-aa' },
  priceList: { name: 'SLA price schedule', shortName: 'Price posting' },
  rip: null,
  cashRounding: null,
  brandRegistration: { label: 'NY brand label registration' },
};

/** Connecticut minimum bottle price add-on (Conn. Gen. Stat. §30-68m(b)): 2¢ up to 200 ml, 4¢ up to 500 ml, 8¢ above. */
export function ctBottleAddOnCents(sizeMl: number | null): number {
  if (sizeMl == null) return 8;
  return sizeMl <= 200 ? 2 : sizeMl <= 500 ? 4 : 8;
}

// Connecticut. Package stores may not sell below cost: the wholesaler's posted bottle price plus delivery
// (§30-68m). One liquor item a month may be posted with DCP at up to 10% below cost; the POS does not
// handle that posting yet, so use a manager close-out for it. Cash rounding is legal from 2027-01-01
// (Public Act 26-128); until then rounding cash totals up breaks the cash discrimination law.
export const CT_RULES: StateRulesPack = {
  code: 'CT',
  name: 'Connecticut',
  needsLegalCheck: true,
  defaultTaxRatePpm: 63500,
  licenseLabel: 'CT permit',
  belowCost: { citation: 'Conn. Gen. Stat. §30-68m', bottleAddOnCents: ctBottleAddOnCents },
  wholesalerCredit: { days: 30, citation: 'Conn. Gen. Stat. §30-48' },
  priceList: { name: 'DCP posted price list', shortName: 'Posted prices' },
  rip: null,
  cashRounding: { notice: 'round totals to the nearest nickel both ways, per Public Act 26-128', from: '2027-01-01' },
  brandRegistration: { label: 'CT brand registration' },
};

const PACKS: Record<string, StateRulesPack> = { NJ: NJ_RULES, NY: NY_RULES, CT: CT_RULES };

export interface UsState { code: string; name: string; system: AlcoholSystem }

/**
 * All 50 states and DC. "control" marks the states that control wholesale and/or retail sale of
 * spirits (NABCA members); Montgomery County, MD also controls but Maryland is a license state.
 */
export const US_STATES: readonly UsState[] = [
  ['AL', 'Alabama', 'control'], ['AK', 'Alaska', 'license'], ['AZ', 'Arizona', 'license'], ['AR', 'Arkansas', 'license'],
  ['CA', 'California', 'license'], ['CO', 'Colorado', 'license'], ['CT', 'Connecticut', 'license'], ['DE', 'Delaware', 'license'],
  ['DC', 'District of Columbia', 'license'], ['FL', 'Florida', 'license'], ['GA', 'Georgia', 'license'], ['HI', 'Hawaii', 'license'],
  ['ID', 'Idaho', 'control'], ['IL', 'Illinois', 'license'], ['IN', 'Indiana', 'license'], ['IA', 'Iowa', 'control'],
  ['KS', 'Kansas', 'license'], ['KY', 'Kentucky', 'license'], ['LA', 'Louisiana', 'license'], ['ME', 'Maine', 'control'],
  ['MD', 'Maryland', 'license'], ['MA', 'Massachusetts', 'license'], ['MI', 'Michigan', 'control'], ['MN', 'Minnesota', 'license'],
  ['MS', 'Mississippi', 'control'], ['MO', 'Missouri', 'license'], ['MT', 'Montana', 'control'], ['NE', 'Nebraska', 'license'],
  ['NV', 'Nevada', 'license'], ['NH', 'New Hampshire', 'control'], ['NJ', 'New Jersey', 'license'], ['NM', 'New Mexico', 'license'],
  ['NY', 'New York', 'license'], ['NC', 'North Carolina', 'control'], ['ND', 'North Dakota', 'license'], ['OH', 'Ohio', 'control'],
  ['OK', 'Oklahoma', 'license'], ['OR', 'Oregon', 'control'], ['PA', 'Pennsylvania', 'control'], ['RI', 'Rhode Island', 'license'],
  ['SC', 'South Carolina', 'license'], ['SD', 'South Dakota', 'license'], ['TN', 'Tennessee', 'license'], ['TX', 'Texas', 'license'],
  ['UT', 'Utah', 'control'], ['VT', 'Vermont', 'control'], ['VA', 'Virginia', 'control'], ['WA', 'Washington', 'license'],
  ['WV', 'West Virginia', 'control'], ['WI', 'Wisconsin', 'license'], ['WY', 'Wyoming', 'control'],
].map(([code, name, system]) => ({ code: code!, name: name!, system: system as AlcoholSystem }));

/** Every state with whether the POS supports it yet, for pickers. */
export function stateList(): (UsState & { supported: boolean; needsLegalCheck: boolean })[] {
  return US_STATES.map((s) => ({ ...s, supported: s.code in PACKS, needsLegalCheck: PACKS[s.code]?.needsLegalCheck ?? false }));
}

export function isSupportedState(code: string): boolean {
  return code in PACKS;
}

/** The rules pack for a store's state. Throws for a state that has no pack yet. */
export function stateRules(code: string | null | undefined): StateRulesPack {
  const pack = PACKS[(code ?? 'NJ').toUpperCase()];
  if (!pack) throw new Error(`No rules pack for state ${code} yet`);
  return pack;
}

/** Whether the store's state lets it round cash totals to 5¢ on `today`. */
export function cashRoundingAllowed(rules: StateRulesPack, today: IsoDate): boolean {
  return !!rules.cashRounding && (!rules.cashRounding.from || today >= rules.cashRounding.from);
}

/** The per-bottle legal floor for a sale: LIFO invoice cost plus any state minimum bottle add-on. */
export function legalFloorUnit(rules: StateRulesPack, unitCostCents: number | null, sizeMl: number | null): number | null {
  if (unitCostCents === null) return null;
  return unitCostCents + (rules.belowCost?.bottleAddOnCents?.(sizeMl) ?? 0);
}
